import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Label, FormGroup } from 'reactstrap';
import moment from 'moment';
import CustomSelect from 'components/CustomSelect';
import CustomInput from 'views/admin/Common/CustomInput';
import { CommonLoader } from 'views/admin/Common/Loader';
import {
  UNOFFICIAL_BILL_PERIODS,
  UNOFFICIAL_BILL_TYPES,
  fetchUnofficialBillsService,
} from 'actions/admin/unofficialBill';
import { showToast } from 'actions/admin/auth';
import { formatMoney, formResMessage, getGlobalSchoolUtils } from 'utils/commonFunc';
import { regNoStatic, courseStatic, PAYMENT_MODES } from 'config/constants';
import ReportTable from '../Transport/reports/ReportTable';

const PERIOD_OPTIONS = [
  { label: 'Monthly', value: UNOFFICIAL_BILL_PERIODS.MONTHLY },
  { label: 'Weekly', value: UNOFFICIAL_BILL_PERIODS.WEEKLY },
  { label: 'Custom', value: UNOFFICIAL_BILL_PERIODS.CUSTOM },
  { label: 'All', value: UNOFFICIAL_BILL_PERIODS.ALL },
];

const labelFor = (options, value) => options.find(el => el.value === value)?.label || value || '--';
const today = () => moment().format('YYYY-MM-DD');

// The "Unofficial" tab of Reports: money collected outside the official fee books, for a month, a week, a date range or
// all time, as summary tiles + a table with Excel export. Kept apart from the Finance tab so these amounts never mix with
// the official totals. Admin only (the Reports page offers the tab to the admin role only). Adding a bill stays on the
// Unofficial Bills page (Finance).
const UnofficialReportPage = () => {
  const dispatch = useDispatch();
  const loggedUserToken = useSelector(state => state.auth.loggedUserToken);
  const loggedUserSchool = useSelector(state => state.auth.loggedUserSchool);
  const schoolId = loggedUserSchool || getGlobalSchoolUtils()?.schoolId;

  const [period, setPeriod] = useState(UNOFFICIAL_BILL_PERIODS.MONTHLY);
  const [month, setMonth] = useState(() => moment().format('YYYY-MM'));
  const [weekDate, setWeekDate] = useState(today);
  const [fromDate, setFromDate] = useState(() => moment().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(today);
  const [searchText, setSearchText] = useState('');
  const [report, setReport] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const showWarning = useCallback((message, type) => dispatch(showToast(message, type)), [dispatch]);

  const periodError = (() => {
    if (period === UNOFFICIAL_BILL_PERIODS.MONTHLY && !month) return 'Select a month';
    if (period === UNOFFICIAL_BILL_PERIODS.WEEKLY && !weekDate) return 'Select a day in the week';
    if (period === UNOFFICIAL_BILL_PERIODS.CUSTOM) {
      if (!fromDate || !toDate) return 'Select both dates';
      if (fromDate > toDate) return 'From date must be on or before the to date';
    }
    return '';
  })();

  useEffect(() => {
    if (!schoolId) return;
    if (periodError) {
      setReport(null);
      return;
    }
    setIsFetching(true);
    fetchUnofficialBillsService({ schoolId, period, month, date: weekDate, fromDate, toDate, token: loggedUserToken })
      .then(data => setReport(data))
      .catch(err => {
        setReport(null);
        showWarning(formResMessage(err), 'error');
      })
      .finally(() => setIsFetching(false));
  }, [schoolId, period, month, weekDate, fromDate, toDate, periodError, loggedUserToken, showWarning]);

  const { columns, rows, summary } = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    const bills = (report?.bills || []).filter(
      item =>
        !search ||
        [item.payerName, item.studentName, item.registerNumber, item.receiptNumber].some(value =>
          (value || '').toLowerCase().includes(search)
        )
    );
    const tableRows = bills.map(item => ({
      receipt: item.receiptNumber || '--',
      date: item.billDate ? moment(item.billDate).format('DD MMM YYYY') : '--',
      payer: item.payerName || '--',
      payerType: item.studentId ? 'Student' : 'Other',
      regNo: item.registerNumber || '--',
      className: item.className || '--',
      type: labelFor(UNOFFICIAL_BILL_TYPES, item.billType),
      mode: labelFor(PAYMENT_MODES, item.paymentMode),
      amount: formatMoney(item.amount),
      remarks: item.remarks || '--',
    }));
    const sum = list => list.reduce((total, item) => total + Number(item.amount || 0), 0);
    const byMode = PAYMENT_MODES.map(mode => ({
      label: mode.label,
      amount: sum(bills.filter(item => item.paymentMode === mode.value)),
    })).filter(item => item.amount > 0);
    return {
      columns: [
        { key: 'receipt', label: 'Receipt No' },
        { key: 'date', label: 'Bill date' },
        { key: 'payer', label: 'Payer' },
        { key: 'payerType', label: 'Payer type' },
        { key: 'regNo', label: regNoStatic },
        { key: 'className', label: courseStatic },
        { key: 'type', label: 'Type' },
        { key: 'mode', label: 'Mode' },
        { key: 'amount', label: 'Amount' },
        { key: 'remarks', label: 'Remarks' },
      ],
      rows: tableRows,
      summary: [
        { label: 'Bills', value: bills.length },
        { label: 'Total collected', value: formatMoney(sum(bills)) },
        ...byMode.map(item => ({ label: `Collected by ${item.label}`, value: formatMoney(item.amount) })),
      ],
    };
  }, [report, searchText]);

  return (
    <>
      <Row>
        <Col sm={6} md={3} xl={2}>
          <FormGroup>
            <Label className="form-label">Period</Label>
            <CustomSelect
              noLabel
              name="period"
              value={period}
              options={PERIOD_OPTIONS}
              onChange={(n, v) => setPeriod(v)}
            />
          </FormGroup>
        </Col>
        {period === UNOFFICIAL_BILL_PERIODS.MONTHLY && (
          <Col sm={6} md={3} xl={2}>
            <CustomInput label="Month" name="month" type="month" value={month} onChange={(n, v) => setMonth(v)} />
          </Col>
        )}
        {period === UNOFFICIAL_BILL_PERIODS.WEEKLY && (
          <Col sm={6} md={3} xl={2}>
            <CustomInput
              label="Any day in the week"
              name="weekDate"
              type="date"
              value={weekDate}
              onChange={(n, v) => setWeekDate(v)}
            />
          </Col>
        )}
        {period === UNOFFICIAL_BILL_PERIODS.CUSTOM && (
          <>
            <Col sm={6} md={3} xl={2}>
              <CustomInput
                label="From date"
                name="fromDate"
                type="date"
                value={fromDate}
                onChange={(n, v) => setFromDate(v)}
              />
            </Col>
            <Col sm={6} md={3} xl={2}>
              <CustomInput
                label="To date"
                name="toDate"
                type="date"
                min={fromDate}
                value={toDate}
                onChange={(n, v) => setToDate(v)}
              />
            </Col>
          </>
        )}
        <Col sm={12} md={6} xl={4}>
          <CustomInput
            label="Search"
            name="searchText"
            placeholder="Payer, student, reg no or receipt no..."
            value={searchText}
            onChange={(n, v) => setSearchText(v)}
          />
        </Col>
      </Row>
      {periodError && <div className="err-msg mb-3">{periodError}</div>}
      {isFetching ? (
        <CommonLoader />
      ) : (
        <ReportTable
          title="Unofficial bills"
          fileName="unofficial_bills"
          emptyText={periodError ? 'Fix the period above to see bills.' : 'No unofficial bills for this period.'}
          columns={columns}
          rows={rows}
          summary={summary}
        />
      )}
    </>
  );
};

export default UnofficialReportPage;
